import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { MaterialModule } from 'src/app/shared/material.module';
import { TranslatePipe } from '@ngx-translate/core';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { AppState } from 'src/app/state/app.reducer';
import { selectSelectableProjects } from 'src/app/state/app.selectors';
import { ClassificationService } from '../../work-classification.service';
import { ClassificationPreviewComponent } from '../classification-preview/classification-preview.component';
import { mapTemplateError } from '../template-error';
import { PreviewNode, projectTreeToPreview } from '../template-tree';
import { WorkClassificationTemplateService } from '../work-classification-template.service';
import {
  WorkClassificationTemplate,
  WorkClassificationTemplateDetail,
  WorkClassificationTemplateInput,
} from '../work-classification-template.models';

export type TemplateFormMode = 'create' | 'edit' | 'fromProject';

export interface TemplateFormDialogData {
  mode: TemplateFormMode;
  template?: WorkClassificationTemplate;
}

/** Business-area hints the backend stores as free text; same labels as the project create form. */
export const BUSINESS_AREA_HINTS: string[] = ['1. Työmaapalvelut', '2. Takuutyöt', '3. Muut palvelut'];

/** Create / edit template details, or create a template from a site's tree. Closes with the saved detail. */
@Component({
  selector: 'app-template-form-dialog',
  templateUrl: './template-form-dialog.component.html',
  styleUrls: ['./template-form-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule, TranslatePipe, AsyncPipe, ClassificationPreviewComponent],
})
export class TemplateFormDialogComponent implements OnInit {
  readonly data = inject<TemplateFormDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<TemplateFormDialogComponent, WorkClassificationTemplateDetail>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private service = inject(WorkClassificationTemplateService);
  private classificationService = inject(ClassificationService);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslateService);
  private logger = inject(LoggerService);
  private store = inject<Store<AppState>>(Store);
  private destroyRef = inject(DestroyRef);

  readonly areas = BUSINESS_AREA_HINTS;
  readonly projects$: Observable<AppState['selectableProjects']> = this.store.select(selectSelectableProjects);

  saving = signal(false);
  previewLoading = signal(false);
  previewNodes = signal<PreviewNode[]>([]);

  form = this.fb.group({
    projectId: this.fb.control<number | null>(null),
    name: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(200)]),
    description: this.fb.nonNullable.control(''),
    businessAreaHint: this.fb.control<string | null>(null),
  });

  ngOnInit(): void {
    const t = this.data.template;
    if (this.data.mode === 'edit' && t) {
      this.form.patchValue({ name: t.name, description: t.description ?? '', businessAreaHint: t.businessAreaHint });
    }
    if (this.data.mode === 'fromProject') {
      this.form.controls.projectId.addValidators(Validators.required);
    }
  }

  get titleKey(): string {
    switch (this.data.mode) {
      case 'edit':
        return 'WORKCLASSIFICATION.TEMPLATES.FORM.TITLE_EDIT';
      case 'fromProject':
        return 'WORKCLASSIFICATION.TEMPLATES.FORM.TITLE_FROM_PROJECT';
      default:
        return 'WORKCLASSIFICATION.TEMPLATES.FORM.TITLE_NEW';
    }
  }

  onProjectChange(projectId: number, projectName: string): void {
    const name = this.form.controls.name;
    if (!name.dirty) {
      name.setValue(projectName);
    }
    this.previewNodes.set([]);
    this.previewLoading.set(true);
    this.classificationService
      .getClassifications(projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: tree => {
          this.previewNodes.set(projectTreeToPreview(tree));
          this.previewLoading.set(false);
        },
        error: err => {
          this.previewLoading.set(false);
          this.logger.errorWithContext('TemplateFormDialog', 'Failed to load project classification tree', err);
          this.showError(err);
        },
      });
  }

  save(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    const input: WorkClassificationTemplateInput = {
      name: v.name.trim(),
      description: v.description.trim() || null,
      businessAreaHint: v.businessAreaHint || null,
    };
    if (!input.name) {
      this.form.controls.name.setErrors({ required: true });
      return;
    }
    this.saving.set(true);

    let request$: Observable<WorkClassificationTemplateDetail>;
    if (this.data.mode === 'edit') {
      request$ = this.service.update(this.data.template!.id, input);
    } else if (this.data.mode === 'fromProject') {
      request$ = this.service.createFromProject(v.projectId!, input);
    } else {
      request$ = this.service.create(input);
    }

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: detail => {
        this.saving.set(false);
        this.dialogRef.close(detail);
      },
      error: err => {
        this.saving.set(false);
        const mapped = mapTemplateError(err);
        if (mapped.code === 'TEMPLATE_NAME_EXISTS') {
          this.form.controls.name.setErrors({ nameExists: true });
          this.form.controls.name.markAsTouched();
        } else if (mapped.code === 'NAME_REQUIRED') {
          this.form.controls.name.setErrors({ required: true });
          this.form.controls.name.markAsTouched();
        } else {
          this.showError(err);
        }
      },
    });
  }

  private showError(err: unknown): void {
    const m = mapTemplateError(err);
    this.snackBar.error(this.translate.instant(m.key, m.params));
  }
}
